import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Truck, RefreshCw, Search, Settings as SettingsIcon, MapPin, Package, AlertTriangle,
  CheckCircle2, Clock, RotateCcw, XCircle, ExternalLink, Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import { fmtDateTz } from "@/lib/format";
import {
  getDashboard, getCounties, listShipments, triggerSync, getSettings,
  STATUS_LABELS, STATUS_COLORS, STATUS_DOT,
} from "@/lib/delivery";
import { formatApiError } from "@/lib/api";

import RomaniaCountyMap, { normalizeCountyName } from "@/pages/delivery/RomaniaCountyMap";
import CountyDetailPanel from "@/pages/delivery/CountyDetailPanel";
import ShipmentDetailDrawer from "@/pages/delivery/ShipmentDetailDrawer";
import ConfirmationQueue from "@/pages/delivery/ConfirmationQueue";
import AnalyticsTab from "@/pages/delivery/AnalyticsTab";
import TodayPanel from "@/pages/delivery/TodayPanel";
import AlertsConfigDialog from "@/pages/delivery/AlertsConfigDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getQueueStats } from "@/lib/delivery";
import { Phone, BarChart3, Bell } from "lucide-react";

const FILTERS = [
  { id: "all",              label: "Toate" },
  { id: "active",           label: "Active" },
  { id: "in_transit",       label: "În tranzit" },
  { id: "out_for_delivery", label: "În livrare" },
  { id: "delivered",        label: "Livrate" },
  { id: "delivery_failed",  label: "Eșuate" },
  { id: "refused",          label: "Refuzate" },
  { id: "returned",         label: "Returnate" },
  { id: "pending",          label: "Nepreluate" },
  { id: "problems",         label: "Problematice" },
];

const RANGES = [
  { id: 7,   label: "7 zile" },
  { id: 30,  label: "30 zile" },
  { id: 60,  label: "60 zile" },
  { id: 90,  label: "90 zile" },
  { id: 180, label: "180 zile" },
];

const HEATMAP_METRICS = [
  { id: "orders",       label: "Total colete" },
  { id: "delivered",    label: "Livrate" },
  { id: "refused",      label: "Refuzate" },
  { id: "returned",     label: "Retur" },
  { id: "rate",         label: "Rată livrare %" },
  { id: "refusal_rate", label: "Rată refuz %" },
  { id: "avg_transit_days", label: "Timp tranzit (zile)" },
];

export default function DeliveryPage() {
  const [days, setDays] = useState(30);
  const [dashboard, setDashboard] = useState(null);
  const [counties, setCounties] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedCountyCode, setSelectedCountyCode] = useState(null);
  const [openAwb, setOpenAwb] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [heatmapMetric, setHeatmapMetric] = useState("orders");
  const [tab, setTab] = useState("tracking");
  const [queueStats, setQueueStats] = useState(null);
  const [alertsCfgOpen, setAlertsCfgOpen] = useState(false);

  // Refresh the queue KPI badge whenever the user visits the module.
  useEffect(() => {
    let cancelled = false;
    getQueueStats()
      .then((s) => { if (!cancelled) setQueueStats(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tab]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, cty, list, st] = await Promise.allSettled([
        getDashboard(days),
        getCounties(days),
        listShipments({ search, status: filter, county: selectedCounty, days, sort, limit: 100 }),
        getSettings(),
      ]);
      if (dash.status === "fulfilled") setDashboard(dash.value);
      if (cty.status === "fulfilled") setCounties(cty.value);
      if (list.status === "fulfilled") {
        setShipments(list.value.items || []);
        setTotal(list.value.total || 0);
      }
      if (st.status === "fulfilled") setSettings(st.value);
      const firstErr = [dash, cty, list, st].find((r) => r.status === "rejected");
      if (firstErr) toast.error(formatApiError(firstErr.reason));
    } finally {
      setLoading(false);
    }
  }, [days, search, filter, selectedCounty, sort]);

  useEffect(() => {
    const t = setTimeout(refresh, 280);
    return () => clearTimeout(t);
  }, [refresh]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await triggerSync(days, true);
      toast.success(r.message || "Sync pornit");
      // Polling for sync result
      setTimeout(refresh, 4000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const k = dashboard?.totals || {};
  const sync = dashboard?.sync || {};

  const credsConfigured = settings?.password_set;
  const needsSetup = !credsConfigured;

  return (
    <div className="space-y-6" data-testid="delivery-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <Truck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Livrări</h1>
            <p className="text-sm text-muted-foreground">
              Tracking AWB-uri GLS · sync automat la 30 min
              {sync?.last_sync_at && (
                <span className="ml-2 text-xs">
                  · Ultimul sync: <span className="font-medium">{fmtDateTz(sync.last_sync_at)}</span>
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-9 w-32" data-testid="delivery-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={onSync}
            disabled={syncing || needsSetup}
            data-testid="delivery-sync-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Se sincronizează…" : "Sincronizează"}
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="delivery-settings-btn">
            <Link to="/setari/livrari">
              <SettingsIcon className="h-4 w-4 mr-2" />
              Setări GLS
            </Link>
          </Button>
        </div>
      </div>

      {needsSetup && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 flex gap-3 items-start" data-testid="delivery-setup-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Credențiale GLS nesetate</div>            <p className="text-sm text-amber-800/90 mt-0.5">
              Configurează credențialele MyGLS pentru a porni sincronizarea statusurilor AWB.
            </p>
          </div>
          <Button asChild size="sm" variant="default">
            <Link to="/setari/livrari">Configurează</Link>
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <TabsList data-testid="delivery-tabs">
            <TabsTrigger value="tracking" data-testid="delivery-tab-tracking">
              <Truck className="h-4 w-4 mr-1.5" /> Tracking colete
            </TabsTrigger>
            <TabsTrigger value="queue" data-testid="delivery-tab-queue">
              <Phone className="h-4 w-4 mr-1.5" /> Coadă confirmare COD
              {queueStats?.pending_total > 0 && (
                <Badge
                  variant="secondary"
                  className={`ml-2 h-5 px-1.5 text-[10px] ${
                    (queueStats?.pending_high || 0) > 0
                      ? "bg-rose-100 text-rose-700"
                      : (queueStats?.pending_medium || 0) > 0
                      ? "bg-amber-100 text-amber-800"
                      : "bg-emerald-100 text-emerald-700"
                  }`}
                  data-testid="queue-tab-badge"
                >
                  {queueStats.pending_total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="delivery-tab-analytics">
              <BarChart3 className="h-4 w-4 mr-1.5" /> Analiză refuzuri
            </TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAlertsCfgOpen(true)}
            data-testid="alerts-config-open-btn"
          >
            <Bell className="h-3.5 w-3.5 mr-1.5" /> Configurare alerte
          </Button>
        </div>

        <TabsContent value="tracking" className="space-y-6 mt-0">
          <TodayPanel
            onFilter={(f, label) => {
              if (f.status) setFilter(f.status);
              else if (f.stuck) setFilter("delivery_failed");
              toast.success(`Filtrat: ${label}`);
            }}
          />
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="delivery-kpis">
        <Kpi icon={Package} label="Total colete" value={k.total ?? 0} hint={`Ultimele ${days} zile`} />
        <Kpi icon={Truck} label="În tranzit" value={k.in_transit ?? 0} accent="sky" />
        <Kpi icon={CheckCircle2} label="Livrate" value={k.delivered ?? 0} hint={`Astăzi: ${k.delivered_today ?? 0}`} accent="emerald" />
        <Kpi icon={Clock} label="În așteptare" value={k.pending ?? 0} accent="amber" />
        <Kpi icon={RotateCcw} label="Refuzate / Retur" value={(k.refused ?? 0) + (k.returned ?? 0)} hint={k.refusal_rate != null ? `${k.refusal_rate.toFixed(1)}%` : "—"} accent="rose" />
        <Kpi icon={XCircle} label="Eșuate" value={k.delivery_failed ?? 0} accent="orange" />
      </div>

      {/* Performance row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <PerfCard
          label="Rată livrare"
          value={k.delivery_rate != null ? `${k.delivery_rate.toFixed(1)}%` : "—"}
          sub="Din coletele finalizate (livrate + retur + refuz)"
        />
        <PerfCard
          label="Timp mediu tranzit"
          value={k.avg_transit_days != null ? `${k.avg_transit_days.toFixed(1)} zile` : "—"}
          sub="De la preluare la livrare reușită"
        />
        <PerfCard
          label="Rată refuz/retur"
          value={k.refusal_rate != null ? `${k.refusal_rate.toFixed(1)}%` : "—"}
          sub="Procent din coletele finalizate care nu au ajuns la client"
          danger={k.refusal_rate != null && k.refusal_rate > 10}
        />
      </div>

      {/* Heatmap row */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              Performanță logistică pe județ
            </div>
            <div className="text-xs text-muted-foreground">
              Hartă interactivă · hover pentru detalii · click pentru analiză aprofundată
            </div>
          </div>
          <Select value={heatmapMetric} onValueChange={setHeatmapMetric}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="delivery-heatmap-metric">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HEATMAP_METRICS.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <RomaniaCountyMap
          data={counties}
          metric={heatmapMetric}
          title={HEATMAP_METRICS.find((m) => m.id === heatmapMetric)?.label}
          selected={selectedCountyCode}
          onSelect={(code) => {
            setSelectedCountyCode(code);
            if (!code) setSelectedCounty("");
          }}
        />

        <CountyDetailPanel
          code={selectedCountyCode}
          row={
            selectedCountyCode
              ? counties.find((c) => normalizeCountyName(c.county) === selectedCountyCode)
              : null
          }
          onClose={() => {
            setSelectedCountyCode(null);
            setSelectedCounty("");
          }}
          onFilter={(countyName) => setSelectedCounty(countyName)}
        />

        {selectedCounty && (
          <div className="mt-3 text-xs">
            Filtrat după județ: <span className="font-semibold">{selectedCounty}</span>
            <button
              className="ml-2 underline text-primary"
              onClick={() => {
                setSelectedCounty("");
                setSelectedCountyCode(null);
              }}
            >
              Elimină
            </button>
          </div>
        )}
      </div>

      {/* Filters + Table */}
      <div className="bg-white border border-border rounded-xl">
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Caută AWB, comandă, client, telefon, oraș…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="delivery-search-input"
              />
            </div>
            <Select value={selectedCounty || "ALL"} onValueChange={(v) => setSelectedCounty(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-9 w-44" data-testid="delivery-county-filter">
                <SelectValue placeholder="Filtru județ" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="ALL">Toate județele</SelectItem>
                {counties.map((c) => (
                  <SelectItem key={c.county} value={c.county}>
                    {c.county} ({c.orders})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-44" data-testid="delivery-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Cele mai noi</SelectItem>
                <SelectItem value="oldest">Cele mai vechi</SelectItem>
                <SelectItem value="transit_desc">Tranzit (descendent)</SelectItem>
                <SelectItem value="attempts_desc">Încercări multiple</SelectItem>
                <SelectItem value="delivered_at_desc">Livrate recent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`delivery-filter-${f.id}`}
                className={`h-7 px-2.5 rounded-md text-xs font-medium border transition-colors ${
                  filter === f.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="delivery-shipments-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                <th className="px-4 py-2.5 font-semibold">AWB</th>
                <th className="px-4 py-2.5 font-semibold">Comandă</th>
                <th className="px-4 py-2.5 font-semibold">Client</th>
                <th className="px-4 py-2.5 font-semibold">Județ / Oraș</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Ultim eveniment</th>
                <th className="px-4 py-2.5 font-semibold">Tranzit</th>
                <th className="px-4 py-2.5 font-semibold">Încercări</th>
              </tr>
            </thead>
            <tbody>
              {loading && shipments.length === 0 ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-t border-border">
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3 bg-secondary rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : shipments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    Niciun colet care să corespundă filtrelor.
                  </td>
                </tr>
              ) : (
                shipments.map((s) => (
                  <tr
                    key={s.awb}
                    onClick={() => setOpenAwb(s.awb)}
                    className="border-t border-border hover:bg-secondary/40 cursor-pointer transition-colors"
                    data-testid={`delivery-row-${s.awb}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{s.awb}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-foreground">{s.order_name || "—"}</div>
                      {s.cod_amount != null && (
                        <div className="text-[11px] text-muted-foreground">COD {Number(s.cod_amount).toFixed(2)} RON</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="truncate max-w-[180px]" title={s.customer_name}>{s.customer_name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{s.customer_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">{s.county || "—"}</div>
                      <div className="text-[11px] text-muted-foreground truncate max-w-[140px]">{s.city || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`gap-1.5 ${STATUS_COLORS[s.status] || ""}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[s.status] || ""}`} />
                        {STATUS_LABELS[s.status] || s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="truncate max-w-[180px]" title={s.status_description}>{s.status_description || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">{s.last_status_at ? fmtDateTz(s.last_status_at) : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {s.transit_days != null ? `${Number(s.transit_days).toFixed(1)} z` : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {s.delivery_attempts ? (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {s.delivery_attempts}
                        </Badge>
                      ) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2.5 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>
            {shipments.length} din {total} colete · ultimele {days} zile
          </span>
          <a
            href="https://gls-group.eu/RO/ro/urmarire-colet"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            GLS Tracking <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      <ShipmentDetailDrawer
        awb={openAwb}
        onClose={() => setOpenAwb(null)}
        onUpdate={refresh}
      />
        </TabsContent>

        <TabsContent value="queue" className="mt-0">
          <ConfirmationQueue shopDomain={settings?.shop_domain} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-0">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>

      <AlertsConfigDialog
        open={alertsCfgOpen}
        onOpenChange={setAlertsCfgOpen}
        onSaved={() => setAlertsCfgOpen(false)}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, accent }) {
  const colors = {
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    orange: "bg-orange-50 text-orange-600",
  }[accent] || "bg-primary/10 text-primary";
  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${colors}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="text-2xl font-bold tracking-tight tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mt-0.5">{label}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function PerfCard({ label, value, sub, danger }) {
  return (
    <div className={`bg-white border rounded-xl p-5 ${danger ? "border-rose-200" : "border-border"}`}>
      <div className={`text-2xl font-bold tabular-nums ${danger ? "text-rose-600" : "text-foreground"}`}>{value}</div>
      <div className="text-sm font-semibold mt-0.5">{label}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}
